import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  BankingEntitiesPaymentStatusEnum,
  BankingEntitiesTransactionStatusEnum,
  BankingValueObjectsTransferStatusEnum,
  PaymentAllTransfersHistoryDocument,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import {
  dateBackendDateTimeToRelativeTime,
  dateBackendDateTimeToUI,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CustomTwoColumnsList from 'modules/attendance/components/CustomTwoColumnsList'
import type { MappedTransferChild } from 'modules/banking/components/PaymentsDataCollection/usePaymentNestedTransfers'
import { VerticalTimeline } from 'modules/banking/components/VerticalTimeline'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'
import AmountWithCurrency from 'modules/expenses/lib/transactions/AmountWithCurrency'
import PageLoading from 'modules/layout/components/Page/Loading'

type IconColor = 'grey600' | 'viridian800' | 'malibu1000' | 'radicalRed800'

type StepId = 'payment_request_creation' | 'payment_creation'

type ValidStatus =
  | BankingEntitiesPaymentStatusEnum
  | BankingEntitiesTransactionStatusEnum
  | BankingValueObjectsTransferStatusEnum

/**
 * Check if an item is a timeline step (not a transfer)
 */
const isStep = (id: number | string): id is StepId => {
  return typeof id === 'string' && (id === 'payment_request_creation' || id === 'payment_creation')
}

/**
 * Normalize status to a valid enum value
 */
const normalizeStatus = (status: string | null | undefined): ValidStatus => {
  if (
    status === 'init' ||
    status === 'pending_consent' ||
    status === 'rejected_consent' ||
    status === 'in_progress' ||
    status === 'rejected' ||
    status === 'done' ||
    status === 'paid' ||
    status === 'partially_paid'
  ) {
    return status
  }
  return 'init'
}

const getRejectionReasonLabel = (reason: string | null | undefined): string => {
  if (!reason) return '-'

  const reasonMap: Record<string, string> = {
    debtor_error: i18n.t('banking.transfers.rejectionReasons.debtorError'),
    creditor_error: i18n.t('banking.transfers.rejectionReasons.creditorError'),
    card_error: i18n.t('banking.transfers.rejectionReasons.cardError'),
    factorial_error: i18n.t('banking.transfers.rejectionReasons.factorialError'),
    transfer_error: i18n.t('banking.transfers.rejectionReasons.transferError'),
    provider_error: i18n.t('banking.transfers.rejectionReasons.providerError'),
    unknown_error: i18n.t('banking.transfers.rejectionReasons.unknownError'),
    technical_error: i18n.t('banking.transfers.rejectionReasons.technicalError'),
    rejected_consent: i18n.t('banking.transfers.rejectionReasons.rejectedConsent'),
  }
  return reasonMap[reason] ?? reason
}

const getStatusIconColor = (status: BankingValueObjectsTransferStatusEnum | string): IconColor => {
  switch (status) {
    case 'paid':
      return 'viridian800'
    case 'rejected':
      return 'radicalRed800'
    case 'in_progress':
      return 'malibu1000'
    case 'init':
    case 'pending':
    default:
      return 'grey600'
  }
}

interface TransferHistoryItem {
  id: number | string
  beneficiaryName?: string | null
  amountCents: number
  currency: string
  createdAt?: string | null
  status?: string | null
  rejectionReason?: string | null
  sourceId?: number | null
  sourceType?: string | null
}

const TransferContent = ({ transfer }: { transfer: TransferHistoryItem }) => {
  const isRejected = transfer.status === 'rejected'

  const listItems = [
    {
      title: i18n.t('banking.attributes.amount'),
      info: <AmountWithCurrency amount={transfer.amountCents} currency={transfer.currency} />,
    },
    {
      title: i18n.t('banking.attributes.created_at'),
      info: transfer.createdAt ? dateBackendDateTimeToUI(transfer.createdAt) : '-',
    },
    ...(isRejected
      ? [
          {
            title: i18n.t('banking.transfers.rejectionReason'),
            info: getRejectionReasonLabel(transfer.rejectionReason),
          },
        ]
      : []),
  ]

  return <CustomTwoColumnsList list={listItems} />
}

export interface TransferHistorySidePanelProps {
  transfer: MappedTransferChild
  onClose: () => void
}

export const TransferHistorySidePanel = ({ transfer, onClose }: TransferHistorySidePanelProps) => {
  // Use ALL execution IDs to show complete history including discarded executions
  const executionIds = transfer._allPaymentExecutionIds ?? transfer._parentPaymentExecutionIds

  const { data, loading, error } = useQuery(PaymentAllTransfersHistoryDocument, {
    variables: {
      paymentExecutionIds: executionIds,
    },
    skip: executionIds.length === 0,
  })

  // Get all transfers and filter to show only those with the same source
  const allTransfers = data?.banking?.transfersConnection?.edges?.map((edge) => edge.node) ?? []

  // Filter transfers by same source (to show history of retries)
  const sourceId = transfer._transferData.sourceId
  const sourceType = transfer._transferData.sourceType
  const relatedTransfers =
    sourceId && sourceType
      ? allTransfers.filter((t) => t.sourceId === sourceId && t.sourceType === sourceType)
      : allTransfers.filter((t) => t.id === transfer.id)

  // Sort by createdAt descending (newest first, oldest last)
  const sortedTransfers = [...relatedTransfers].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
    return dateB - dateA
  })

  // Find the oldest transfer (creation of payment request or outstanding payment)
  const oldestTransfer = sortedTransfers[sortedTransfers.length - 1]
  const paymentRequestCreationDate = oldestTransfer?.createdAt || transfer._transferData.createdAt

  // Find the first transfer that was added to a payment (has paymentExecutionId)
  // This represents when the transfer was added to the payment
  const firstPaymentTransfer = sortedTransfers
    .slice()
    .reverse()
    .find((t) => t.paymentExecutionId != null)
  const paymentCreationDate = firstPaymentTransfer?.createdAt

  // Create step 1: Payment request / Outstanding payment creation
  const paymentRequestStep: TransferHistoryItem = {
    id: 'payment_request_creation',
    beneficiaryName: transfer._transferData.beneficiaryName,
    amountCents: transfer.amountCents,
    currency: transfer.currency,
    createdAt: paymentRequestCreationDate,
    status: null,
    rejectionReason: null,
    sourceId: sourceId ?? null,
    sourceType: sourceType ?? null,
  }

  // Create step 2: Transfer added to payment (only if it exists)
  const paymentStep: TransferHistoryItem | null = paymentCreationDate
    ? {
        id: 'payment_creation',
        beneficiaryName: transfer._transferData.beneficiaryName,
        amountCents: transfer.amountCents,
        currency: transfer.currency,
        createdAt: paymentCreationDate,
        status: null,
        rejectionReason: null,
        sourceId: sourceId ?? null,
        sourceType: sourceType ?? null,
      }
    : null

  // Newest transfers first (at top), steps at the bottom (oldest)
  // Order: newest transfers -> payment step -> payment request step (oldest)
  const timelineItems = [
    ...sortedTransfers,
    ...(paymentStep ? [paymentStep] : []),
    paymentRequestStep,
  ]

  // Timeline helper functions
  const getTimelineKey = (item: TransferHistoryItem): number | string => item.id

  const getTimelineTitle = (item: TransferHistoryItem): string => {
    if (isStep(item.id)) {
      const titleMap: Record<StepId, string> = {
        payment_request_creation: i18n.t(
          'banking.transfers.historySidePanel.paymentRequestCreation'
        ),
        payment_creation: i18n.t('banking.transfers.historySidePanel.paymentCreation'),
      }
      return titleMap[item.id]
    }
    return item.beneficiaryName ?? '-'
  }

  const getTimelineStatusLabel = (item: TransferHistoryItem): string => {
    if (isStep(item.id)) {
      return ''
    }
    return statusText(normalizeStatus(item.status))
  }

  const getTimelineStatusType = (
    item: TransferHistoryItem
  ): 'neutral' | 'info' | 'positive' | 'warning' | 'critical' => {
    if (isStep(item.id)) {
      return 'neutral'
    }
    return statusLabelType(normalizeStatus(item.status))
  }

  const getTimelineIconColor = (item: TransferHistoryItem): IconColor => {
    if (isStep(item.id)) {
      return 'grey600'
    }
    return getStatusIconColor(item.status ?? 'init')
  }

  const getTimelineRelativeTime = (item: TransferHistoryItem): string | null => {
    if (!item.createdAt) {
      return null
    }
    return dateBackendDateTimeToRelativeTime(item.createdAt)
  }

  const renderTimelineContent = (item: TransferHistoryItem): React.ReactNode => {
    if (isStep(item.id)) {
      return (
        <Box width="full">
          <Text.Normal color="grey700">
            {item.createdAt
              ? stringToDate(item.createdAt, 'backend_datetime').format('L HH:mm:ss')
              : '-'}
          </Text.Normal>
        </Box>
      )
    }
    return <TransferContent transfer={item} />
  }

  const content = () => {
    if (loading) {
      return <PageLoading />
    }

    if (error) {
      return <NotFound />
    }

    return (
      <Box width="full" gap="s24">
        <Box width="full" gap="s8">
          <Text.Normal weight="medium" size="200" color="grey700">
            {i18n.t('banking.attributes.beneficiary')}
          </Text.Normal>
          <Text.Header2 color="grey1000">{transfer._transferData.beneficiaryName}</Text.Header2>
        </Box>

        <Box width="full" gap="s8">
          <Text.Normal weight="medium" size="200" color="grey700">
            {i18n.t('banking.attributes.iban')}
          </Text.Normal>
          <Text.Normal color="grey1000">{transfer._transferData.accountNumber || '-'}</Text.Normal>
        </Box>

        <Box width="full" gap="s8">
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('banking.paymentRequests.itemTransfersSidePanel.transfersTimeline')}
          </Text.Normal>
          <Box width="full" paddingTop="s8">
            <VerticalTimeline
              items={timelineItems}
              getKey={getTimelineKey}
              getTitle={getTimelineTitle}
              getStatusLabel={getTimelineStatusLabel}
              getStatusType={getTimelineStatusType}
              getIconColor={getTimelineIconColor}
              getRelativeTime={getTimelineRelativeTime}
              renderContent={renderTimelineContent}
            />
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      title={i18n.t('banking.transfers.historySidePanel.title')}
      open
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
    >
      {content()}
    </Sidepanel>
  )
}
